import { colors } from '@/styles';
import PlaceIcon from '@mui/icons-material/Place';
import { format } from 'date-fns';
import { css } from '@emotion/react';
import {
    MANIFEST_STATUSES,
    MANIFEST_STATUS_LABELS,
    MANIFEST_TYPE_LABELS,
    RECEIVED_STATUSES,
    RETURN_MANIFEST_TYPE,
    STAGED_STATUSES,
    manifestDisplayType,
} from '@/constants/manifestStatuses';
import { startCase } from 'lodash';
import { formatInTimeZone } from 'date-fns-tz';
import { EXCEPTION_TYPES } from '@onward-delivery/core';

const BASE_COLUMNS = [
    {
        Header: 'Manifest',
        id: 'manifest_number',
        width: 140,
        accessor: (manifest) => manifest.manifest_number,
    },
    {
        Header: 'Name',
        id: 'name',
        width: 220,
        accessor: (manifest) => manifest.name || '--',
    },
    {
        Header: 'Type',
        id: 'type',
        width: 140,
        accessor: (manifest) => MANIFEST_TYPE_LABELS[manifestDisplayType(manifest)] || '--',
    },
];

const STATUS_COLUMN_GROUP = {
    Header: 'Manifest Right',
    id: 'manifest-right-group',
    sticky: 'right',
    columns: [
        {
            Header: 'Status',
            id: 'status',
            width: 150,
            Cell: ({ row, callbacks }) => {
                const { status } = row.original;
                return (
                    <span>
                        <PlaceIcon
                            css={css`
                                color: ${MANIFEST_STATUS_LABELS[status]?.color || colors.greys.secondary};
                            `}
                        />
                        {MANIFEST_STATUS_LABELS[status]?.label || 'Unknown'}
                    </span>
                );
            },
        },
    ],
};

const INBOUND_COLUMNS = [
    {
        Header: 'Manifest Main',
        id: 'manifest-main-group',
        columns: [
            ...BASE_COLUMNS,
            {
                Header: 'Shipper',
                id: 'shipper',
                width: 175,
                accessor: (manifest) => {
                    const shippers = [
                        ...new Set(
                            (manifest?.items || []).reduce((itemAcc, i) => {
                                const shipperName = i?.item?.order?.order_shipper?.business_name;
                                if (shipperName) {
                                    itemAcc.push(shipperName);
                                }
                                return itemAcc;
                            }, [])
                        ),
                    ];
                    return (
                        <div
                            css={css`
                                overflow: auto;
                                white-space: nowrap;
                            `}
                        >
                            {shippers?.length ? shippers.join(', ') : '--'}
                        </div>
                    );
                },
            },
            {
                Header: 'Receiving Date',
                id: 'receiving_date',
                width: 190,
                accessor: (manifest) => {
                    // Format like the Scheduled Delivery column; route-linked manifests otherwise show the MM/dd/yyyy @client value.
                    const dateToFormat = manifest.route?.scheduled_delivery || manifest.receiving_date;
                    return dateToFormat ? formatInTimeZone(new Date(dateToFormat), 'UTC', 'EEE, MMM d, yyyy') : '--';
                },
            },
            {
                Header: 'Cu. Ft.',
                id: 'cubic_feet',
                width: 140,
                accessor: (manifest) => (manifest.cubes ? parseInt(manifest.cubes) : 0),
            },
            {
                Header: 'Orders',
                id: 'orders',
                width: 140,
                accessor: (manifest) => manifest.orders?.length || 0,
            },
            {
                Header: 'Items',
                id: 'items',
                width: 140,
                accessor: (manifest) => (manifest?.total ? parseInt(manifest.total) : 0),
            },
            {
                Header: 'Received',
                id: 'received',
                width: 150,
                accessor: (manifest) =>
                    `${manifest?.received ? parseInt(manifest.received) : 0} / ${
                        manifest?.total ? parseInt(manifest.total) : 0
                    }`,
            },
            // {
            //     Header: 'Origin',
            //     id: 'origin',
            //     width: 150,
            //     accessor: (manifest) => manifest.origin || '--',
            // },
            // {
            //     Header: 'Destination',
            //     id: 'destination',
            //     width: 150,
            //     accessor: (manifest) => manifest.destination || '--',
            // },
        ],
    },
    STATUS_COLUMN_GROUP,
];

const OUTBOUND_COLUMNS = [
    {
        Header: 'Manifest Main',
        id: 'manifest-main-group',
        columns: [
            ...BASE_COLUMNS,
            {
                Header: 'Shipper',
                id: 'shipper',
                width: 175,
                accessor: (manifest) => {
                    const shippers = manifest?.route
                        ? [
                              ...new Set(
                                  (manifest?.route?.orders || []).reduce((orderAcc, o) => {
                                      const shipperName = o?.order?.order_shipper?.business_name;
                                      if (shipperName) {
                                          orderAcc.push(shipperName);
                                      }
                                      return orderAcc;
                                  }, [])
                              ),
                          ]
                        : [
                              ...new Set(
                                  (manifest?.items || []).reduce((itemAcc, i) => {
                                      const shipperName = i?.item?.order?.order_shipper?.business_name;
                                      if (shipperName) {
                                          itemAcc.push(shipperName);
                                      }
                                      return itemAcc;
                                  }, [])
                              ),
                          ];
                    return shippers?.length ? shippers.join(', ') : '--';
                },
                Cell: ({ value }) => {
                    return (
                        <div
                            css={css`
                                overflow: hidden;
                                text-overflow: ellipsis;
                            `}
                        >
                            {value}
                        </div>
                    );
                },
            },
            {
                Header: 'Route',
                id: 'route',
                width: 140,
                accessor: (manifest) => manifest.route?.route_number || '--',
            },
            {
                Header: 'Resource',
                id: 'resource',
                width: 150,
                accessor: (manifest) => manifest.route?.resource?.name || '--',
                Cell: ({ value }) => {
                    return (
                        <div
                            css={css`
                                overflow: hidden;
                                text-overflow: ellipsis;
                            `}
                        >
                            {value}
                        </div>
                    );
                },
            },
            {
                Header: 'Driver',
                id: 'driver',
                width: 150,
                accessor: (manifest) => manifest.route?.driver?.username || '--',
            },
            {
                Header: 'Scheduled Date',
                id: 'scheduled_delivery',
                width: 170,
                accessor: (manifest) => {
                    const dateToFormat = manifest.route?.scheduled_delivery || manifest.receiving_date;
                    return dateToFormat ? formatInTimeZone(new Date(dateToFormat), 'UTC', 'EEE, MMM d, yyyy') : '--';
                },
            },
            {
                Header: 'Cu. Ft.',
                id: 'cubic_feet',
                width: 140,
                accessor: (manifest) => (manifest.cubes ? parseInt(manifest.cubes) : 0),
            },
            {
                Header: 'Orders',
                id: 'orders',
                width: 140,
                accessor: (manifest) => manifest.orders?.length || 0,
            },
            {
                Header: 'Items',
                id: 'items',
                width: 140,
                accessor: (manifest) => manifest.total || 0,
            },
            {
                Header: 'Staged',
                id: 'staged',
                width: 100,
                accessor: (manifest) => `${manifest.staged || 0} / ${manifest.total || 0}`,
            },
        ],
    },
    STATUS_COLUMN_GROUP,
];

export const TABS = {
    INBOUND: {
        value: 'INBOUND',
        label: 'Inbound',
        columns: INBOUND_COLUMNS,
        statusOptions: [MANIFEST_STATUSES.NOT_RECEIVED, MANIFEST_STATUSES.IN_PROGRESS, MANIFEST_STATUSES.RECEIVED],
        typeOptions: ['INBOUND', RETURN_MANIFEST_TYPE, 'CROSS_DOCK'],
        defaultFilters: { status: 'ACTIVE' }, // Changed from NOT_RECEIVED to ACTIVE
        activeStatuses: [MANIFEST_STATUSES.NOT_RECEIVED, MANIFEST_STATUSES.IN_PROGRESS, MANIFEST_STATUSES.RECEIVED],
    },
    OUTBOUND: {
        value: 'OUTBOUND',
        label: 'Outbound',
        columns: OUTBOUND_COLUMNS,
        statusOptions: [
            MANIFEST_STATUSES.AWAITING_STAGING,
            MANIFEST_STATUSES.IN_PROGRESS,
            MANIFEST_STATUSES.STAGED,
            MANIFEST_STATUSES.COMPLETE,
        ],
        typeOptions: ['OUTBOUND', 'RETURN_TO_SENDER', 'WILL_CALL'],
        defaultFilters: { status: 'ACTIVE' }, // Changed from NOT_RECEIVED to ACTIVE
        activeStatuses: [MANIFEST_STATUSES.AWAITING_STAGING, MANIFEST_STATUSES.IN_PROGRESS, MANIFEST_STATUSES.STAGED],
    },
    REQUESTS: {
        value: 'REQUESTS',
        label: 'Requests',
        columns: OUTBOUND_COLUMNS,
        statusOptions: [],
        typeOptions: ['WILL_CALL'],
        defaultFilters: {},
        activeStatuses: [],
    },
    EXTERNAL: {
        value: 'EXTERNAL',
        label: 'External Inbounds',
        columns: INBOUND_COLUMNS,
        statusOptions: [MANIFEST_STATUSES.NOT_RECEIVED, MANIFEST_STATUSES.IN_PROGRESS, MANIFEST_STATUSES.RECEIVED],
        typeOptions: ['INBOUND', RETURN_MANIFEST_TYPE, 'CROSS_DOCK'],
    },
    EXTERNALOUT: {
        value: 'EXTERNALOUT',
        label: 'External Outbounds',
        columns: OUTBOUND_COLUMNS,
        statusOptions: [
            MANIFEST_STATUSES.AWAITING_STAGING,
            MANIFEST_STATUSES.IN_PROGRESS,
            MANIFEST_STATUSES.STAGED,
            MANIFEST_STATUSES.COMPLETE,
        ],
        typeOptions: ['OUTBOUND', 'RETURN_TO_SENDER', 'WILL_CALL'],
    },
};

export const DAYS_30 = 2592000000;

export const MANIFEST_SEARCHABLE = [
    'manifest_number',
    'name',
    'origin',
    'destination',
    'items.item.sku',
    'items.item.description',
    'items.item.serial_number',
    'items.item.barcode',
    'items.item.line_id',
    'items.item.order.order_number',
    'items.item.order.po_number',
    'items.item.order.reference_id',
    'items.item.order.order_shipper.business_name',
    'route.orders.order.order_shipper.business_name',
    'route.orders.order.po_number',
    'route.orders.order.reference_id',
    'route.orders.order.order_number',
];
export const ORDER_SEARCHABLE = [
    'order_number',
    'po_number',
    'reference_id',
    'dropoff_name',
    'itemsByOrderId.sku',
    'itemsByOrderId.description',
    'itemsByOrderId.pallet.pallet_number',
    'itemsByOrderId.pallet.pallet_name',
    'order_shipper.business_name',
];
